// ─────────────────────────────────────────────────────────────────
//  PatientSidebar.jsx  —  v3  Clinical Clarity enhanced
//  • Gradient active pill with soft blue glow (replaces flat tint)
//  • Brand block with subtle logo glow, "MENU" section label
//  • Patient chip with live status dot
//  Exports unchanged: DesktopSidebar, MobileDrawer, BottomTabBar, default
// ─────────────────────────────────────────────────────────────────
import React from "react";
import { Avatar, Typography } from "antd";
import {
  DashboardOutlined, CalendarOutlined,
  UserOutlined, LogoutOutlined,
  MedicineBoxOutlined, LeftOutlined, RightOutlined,
  ExperimentOutlined, HistoryOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const PHP_APP_URL = process.env.REACT_APP_PHP_URL || "https://hmsv3atlantispediatrichospital.com/";

// ── Tokens (inline — no import conflicts) ─────────────────────
const G700   = "#0E5BC2";
const G500   = "#4189DC";
const G100   = "#DBEEFF";
const G50    = "#F0F7FF";
const GRAY6  = "#E3EBF3";
const GRAY7  = "#F0F4F8";
const SLATE  = "#0F1C2E";
const MUTED  = "#5E718D";
const WHITE  = "#FFFFFF";
const GREEN_MID = "#22A66A";
const GRAD_BRAND = `linear-gradient(135deg, ${G700}, ${G500})`;
const FF_DISPLAY = "'Figtree','Inter',system-ui,sans-serif";
const FF_BODY    = "'Inter',system-ui,sans-serif";
const FF_MONO    = "'JetBrains Mono','Fira Code',monospace";

const NAV_ITEMS = [
  { key: "overview",      icon: <DashboardOutlined />,  label: "Overview"      },
  { key: "appointments",  icon: <CalendarOutlined />,   label: "Appointments"  },
  { key: "visits",        icon: <HistoryOutlined />,    label: "Visit History" },
  { key: "investigation", icon: <ExperimentOutlined />, label: "Investigation" },
  { key: "profile",       icon: <UserOutlined />,       label: "My Profile"    },
];

// ── Nav item ───────────────────────────────────────────────────
function NavItem({ item, active, collapsed, onClick }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      onClick={() => onClick(item.key)}
      title={collapsed ? item.label : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position:       "relative",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "flex-start",
        gap:            collapsed ? 0 : 10,
        width:          "100%",
        minHeight:      44,
        padding:        collapsed ? "0 11px" : "0 10px",
        border:         "none",
        borderRadius:   10,
        /* Active = gradient pill; hover = soft tint */
        background:     active ? GRAD_BRAND : hovered ? G50 : "transparent",
        color:          active ? WHITE : hovered ? G700 : MUTED,
        boxShadow:      active ? "0 6px 16px rgba(14,91,194,0.28)" : "none",
        fontFamily:     FF_BODY,
        fontSize:       13.5,
        fontWeight:     active ? 600 : 500,
        cursor:         "pointer",
        transition:     "background 0.18s, color 0.18s, box-shadow 0.18s, transform 0.12s",
        whiteSpace:     "nowrap",
        overflow:       "hidden",
        textAlign:      "left",
        transform:      hovered && !active ? "translateX(2px)" : "none",
      }}
    >
      {/* Icon box — fixed 32×32, never shrinks */}
      <span style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        width:          32,
        height:         32,
        minWidth:       32,
        borderRadius:   8,
        background:     active ? "rgba(255,255,255,0.18)" : "transparent",
        color:          "inherit",
        fontSize:       16,
        flexShrink:     0,
        transition:     "background 0.18s",
      }}>
        {item.icon}
      </span>

      {!collapsed && (
        <span style={{
          flex:         1,
          overflow:     "hidden",
          textOverflow: "ellipsis",
          whiteSpace:   "nowrap",
          lineHeight:   1,
          letterSpacing: "-0.1px",
        }}>
          {item.label}
        </span>
      )}
    </button>
  );
}

// ── Shared sidebar body ────────────────────────────────────────
function SidebarBody({
  patient, decoded, activeNav,
  onNav, onLogout, collapsed, dependentSlot,
}) {
  const displayName = patient?.full_name || decoded?.fullName || "Patient";
  const prn         = patient?.prn       || decoded?.prn       || "";
  const initial     = displayName[0]?.toUpperCase() || "P";
  const photoUrl    = patient?.photo
    ? `${PHP_APP_URL}/uploads/patients/img/${patient.photo}`
    : null;

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%", width: "100%", overflow: "hidden",
    }}>

      {/* ── Brand ──────────────────────────────────────────── */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap:            10,
        padding:        collapsed ? "16px 0 14px" : "16px 14px 14px",
        borderBottom:   `1px solid ${GRAY6}`,
        flexShrink:     0,
        minHeight:      66,
        overflow:       "hidden",
      }}>
      <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: 11,
    flexShrink: 0,
    boxShadow: "0 6px 16px rgba(14,91,194,0.35)",
    overflow: "hidden",
  }}
>
  <img
    src="https://atlantispediatrichospital.com/wp-content/uploads/2023/05/APH_Logo_1-removebg-preview.png"
    alt="Atlantis Pediatric Hospital"
    style={{
      width: "30px",
      height: "30px",
      objectFit: "contain",
    }}
  />
</div>

        {!collapsed && (
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <Text style={{
              color:         SLATE,
              fontWeight:    700,
              fontSize:      14,
              display:       "block",
              lineHeight:    1.25,
              letterSpacing: "-0.3px",
              whiteSpace:    "nowrap",
              fontFamily:    FF_DISPLAY,
            }}>
              Atlantis Pediatric
            </Text>
            <Text style={{
              color:         MUTED,
              fontSize:      8.5,
              letterSpacing: "1.1px",
              fontWeight:    700,
              textTransform: "uppercase",
              whiteSpace:    "nowrap",
              fontFamily:    FF_BODY,
            }}>
              Patient Portal
            </Text>
          </div>
        )}
      </div>

      {/* ── Patient chip ───────────────────────────────────── */}
      <div style={{
        padding:        "10px 10px",
        flexShrink:     0,
        display:        "flex",
        alignItems:     "center",
        justifyContent: collapsed ? "center" : "flex-start",
        borderBottom:   `1px solid ${GRAY7}`,
      }}>
        {collapsed ? (
          <div style={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              size={36}
              src={photoUrl || undefined}
              style={{
                background: GRAD_BRAND,
                fontSize: 13, fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
            <span style={{
              position: "absolute", bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: "50%",
              background: GREEN_MID, border: `2px solid ${WHITE}`,
            }} />
          </div>
        ) : (
          <div style={{
            display:      "flex",
            alignItems:   "center",
            gap:          10,
            background:   `linear-gradient(180deg, ${WHITE}, ${G50})`,
            borderRadius: 12,
            padding:      "10px 11px",
            border:       `1px solid ${G100}`,
            width:        "100%",
            boxShadow:    "0 1px 3px rgba(15,28,46,0.05)",
          }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                size={36}
                src={photoUrl || undefined}
                style={{
                  background: GRAD_BRAND,
                  fontSize: 13, fontWeight: 700,
                }}
              >
                {initial}
              </Avatar>
              <span style={{
                position: "absolute", bottom: 0, right: 0,
                width: 10, height: 10, borderRadius: "50%",
                background: GREEN_MID, border: `2px solid ${WHITE}`,
              }} />
            </div>
            <div style={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
              <Text style={{
                color:        SLATE, fontWeight: 600,
                fontSize:     12.5, display: "block",
                whiteSpace:   "nowrap", overflow: "hidden",
                textOverflow: "ellipsis", letterSpacing: "-0.1px",
                fontFamily:   FF_BODY,
              }}>
                {displayName}
              </Text>
              <Text style={{
                color:      MUTED, fontSize: 10,
                fontFamily: FF_MONO, display: "block",
                whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {prn}
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* ── Dependent switcher slot ─────────────────────────── */}
      {dependentSlot && (
        <div style={{
          padding:      "8px 10px",
          borderBottom: `1px solid ${GRAY7}`,
          flexShrink:   0,
        }}>
          {dependentSlot}
        </div>
      )}

      {/* ── Nav items ──────────────────────────────────────── */}
      <nav style={{
        flex:          1,
        overflowY:     "auto",
        overflowX:     "hidden",
        padding:       "10px 8px 8px",
        display:       "flex",
        flexDirection: "column",
        gap:           3,
      }}>
        {!collapsed && (
          <Text style={{
            color: MUTED, fontSize: 9.5, fontWeight: 700,
            letterSpacing: "1.2px", textTransform: "uppercase",
            fontFamily: FF_BODY, padding: "0 10px 6px",
            display: "block",
          }}>
            Menu
          </Text>
        )}
        {NAV_ITEMS.map(item => (
          <NavItem
            key={item.key}
            item={item}
            active={activeNav === item.key}
            collapsed={collapsed}
            onClick={onNav}
          />
        ))}
      </nav>

      {/* ── Sign Out ───────────────────────────────────────── */}
      <div style={{
        padding:    "8px 8px 20px",
        borderTop:  `1px solid ${GRAY6}`,
        flexShrink: 0,
      }}>
        <button
          onClick={onLogout}
          title={collapsed ? "Sign Out" : undefined}
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "flex-start",
            gap:            collapsed ? 0 : 10,
            width:          "100%",
            minHeight:      44,
            padding:        collapsed ? "0 11px" : "0 10px",
            border:         "none",
            borderRadius:   10,
            background:     "transparent",
            color:          "#ef4444",
            fontFamily:     FF_BODY,
            fontSize:       13.5,
            fontWeight:     500,
            cursor:         "pointer",
            transition:     "background 0.15s",
            textAlign:      "left",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "#fff0f0")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          <span style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            width:          32,
            height:         32,
            minWidth:       32,
            borderRadius:   8,
            background:     "#fff0f0",
            color:          "#ef4444",
            fontSize:       15,
            flexShrink:     0,
          }}>
            <LogoutOutlined />
          </span>

          {!collapsed && (
            <span style={{ lineHeight: 1 }}>Sign Out</span>
          )}
        </button>
      </div>

    </div>
  );
}

// ── Desktop sidebar ────────────────────────────────────────────
export function DesktopSidebar({
  patient, decoded, activeNav, onNav,
  onLogout, collapsed, onToggleCollapse, dependentSlot,
}) {
  return (
    <div style={{
      position:      "relative",
      width:         collapsed ? 68 : 252,
      minWidth:      collapsed ? 68 : 252,
      height:        "100%",
      background:    WHITE,
      borderRight:   `1px solid ${GRAY6}`,
      display:       "flex",
      flexDirection: "column",
      flexShrink:    0,
      transition:    "width 0.28s cubic-bezier(0.16,1,0.3,1), min-width 0.28s cubic-bezier(0.16,1,0.3,1)",
      zIndex:        100,
      overflow:      "visible",
    }}>

      {/* Collapse toggle */}
      <button
        onClick={onToggleCollapse}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{
          position:       "absolute",
          top:            74,
          right:          -13,
          width:          26,
          height:         26,
          borderRadius:   "50%",
          background:     WHITE,
          border:         `1px solid ${GRAY6}`,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          fontSize:       10,
          color:          MUTED,
          cursor:         "pointer",
          zIndex:         10,
          boxShadow:      "0 1px 4px rgba(15,28,46,0.10)",
          transition:     "background 0.15s, color 0.15s, transform 0.15s",
          flexShrink:     0,
          padding:        0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background  = G50;
          e.currentTarget.style.color       = G700;
          e.currentTarget.style.borderColor = G100;
          e.currentTarget.style.transform   = "scale(1.08)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background  = WHITE;
          e.currentTarget.style.color       = MUTED;
          e.currentTarget.style.borderColor = GRAY6;
          e.currentTarget.style.transform   = "scale(1)";
        }}
      >
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </button>

      <SidebarBody
        patient={patient}
        decoded={decoded}
        activeNav={activeNav}
        onNav={onNav}
        onLogout={onLogout}
        collapsed={collapsed}
        dependentSlot={dependentSlot}
      />
    </div>
  );
}

// ── Mobile drawer ──────────────────────────────────────────────
export function MobileDrawer({
  patient, decoded, activeNav, onNav,
  onLogout, open, onClose,
}) {
  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position:             "fixed",
            inset:                0,
            background:           "rgba(10,20,40,0.42)",
            backdropFilter:       "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            zIndex:               190,
            animation:            "pdFade 0.2s ease both",
          }}
        />
      )}
      <div style={{
        position:      "fixed",
        top:           0,
        left:          0,
        height:        "100%",
        width:         276,
        background:    WHITE,
        zIndex:        200,
        boxShadow:     "0 16px 40px rgba(15,28,46,0.14)",
        transform:     open ? "translateX(0)" : "translateX(-100%)",
        transition:    "transform 0.3s cubic-bezier(0.16,1,0.3,1)",
        display:       "flex",
        flexDirection: "column",
        overflow:      "hidden",
      }}>
        <SidebarBody
          patient={patient}
          decoded={decoded}
          activeNav={activeNav}
          onNav={(key) => { onNav(key); onClose(); }}
          onLogout={onLogout}
          collapsed={false}
        />
      </div>
    </>
  );
}

// ── Bottom tab bar ─────────────────────────────────────────────
export function BottomTabBar({ activeNav, onNav }) {
  const tabs = [
    { key: "overview",        icon: <DashboardOutlined />,   label: "Home"    },
    { key: "appointments",    icon: <CalendarOutlined />,    label: "Appts"   },
    { key: "visits", icon: <MedicineBoxOutlined />, label: "Records" },
    { key: "investigation",   icon: <ExperimentOutlined />,  label: "Labs"    },
    { key: "profile",         icon: <UserOutlined />,        label: "Profile" },
  ];

  return (
    <div className="pd-bottom-nav">
      <div style={{
        display:        "flex",
        justifyContent: "space-around",
        maxWidth:       520,
        margin:         "0 auto",
      }}>
        {tabs.map(t => {
          const isActive = activeNav === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onNav(t.key)}
              style={{
                display:       "flex",
                flexDirection: "column",
                alignItems:    "center",
                gap:           3,
                border:        "none",
                background:    "none",
                minHeight:     44,
                padding:       "8px 14px 7px",
                color:         isActive ? G700 : "#b0bec5",
                fontFamily:    FF_BODY,
                cursor:        "pointer",
                flex:          1,
                position:      "relative",
                transition:    "color 0.16s, transform 0.1s",
              }}
            >
              <span style={{
                position:     "absolute",
                top:          0,
                left:         "50%",
                transform:    `translateX(-50%) scaleX(${isActive ? 1 : 0})`,
                width:        28,
                height:       3,
                background:   GRAD_BRAND,
                borderRadius: "0 0 4px 4px",
                transition:   "transform 0.2s cubic-bezier(0.16,1,0.3,1)",
              }} />
              <span style={{
                fontSize: 20, lineHeight: 1,
                transform: isActive ? "translateY(-1px)" : "none",
                transition: "transform 0.2s cubic-bezier(0.16,1,0.3,1)",
              }}>
                {t.icon}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PatientSidebar(props) {
  return <DesktopSidebar {...props} />;
}

export default PatientSidebar;